import React, { useContext, useState, useEffect } from "react";
import { getTaskStatus } from "../utils/helper";
import Modal from "../../../components/controls/modal/Modal";
import { ResourceMoniteringContext } from "../../resourcePlanner/resourceMonitoring/ResourceContext";
import { Select } from "@fluentui/react-components";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";
import { Plus, Trash2 } from "lucide-react";

export function DependencyModal({ task, onSave, onClose }) {
  const { scopes } = useContext(ResourceMoniteringContext);
  const [links, setLinks] = useState([{ id: null, type: null, scopeId: null }]);
  const [existingDependencies, setExistingDependencies] = useState(
    task.dependencies || []
  );
  const [selectedScope, setSelectedScope] = useState(null);
  const [availableTasksForScope, setAvailableTasksForScope] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (scopes && scopes.length > 0 && !selectedScope) {
      setSelectedScope(task?.scopeId ?? scopes[0].id);
    }
  }, [scopes, selectedScope]);

  const calculateAvailableTasksForScope = (
    scopeId,
    currentLinks,
    currentDependencies
  ) => {
    if (!scopeId || !scopes) return [];
    const currentScope = scopes.find((scope) => scope.id === scopeId);
    if (!currentScope) return [];

    if (!currentScope.tasks || currentScope.tasks.length === 0) {
      return [];
    }

    const filteredTasks = currentScope.tasks.filter((scopeTask) => {
      const isCurrentTask = scopeTask.id === task.id;
      const isExistingDependency = currentDependencies.some(
        (dep) => dep.task.id === scopeTask.id
      );
      const isInCurrentLinks = currentLinks.some(
        (link) => link.id === scopeTask.id && link.type !== null
      );

      return !isCurrentTask && !isExistingDependency && !isInCurrentLinks;
    });
    return filteredTasks;
  };

  const getScopeInfo = (scopeId) => {
    if (!scopeId || !scopes)
      return { hasScope: false, hasTasks: false, taskCount: 0 };

    const currentScope = scopes.find((scope) => scope.id === scopeId);
    if (!currentScope)
      return { hasScope: false, hasTasks: false, taskCount: 0 };

    const taskCount = currentScope.tasks ? currentScope.tasks.length : 0;
    return {
      hasScope: true,
      hasTasks: taskCount > 0,
      taskCount: taskCount,
      scopeName: currentScope.name,
    };
  };

  useEffect(() => {
    const availableTasks = calculateAvailableTasksForScope(
      selectedScope,
      links,
      existingDependencies
    );
    setAvailableTasksForScope(availableTasks);
  }, [selectedScope, scopes, task.id]);

  const dependencyTypeMapper = {
    0: "Finish To Start",
    1: "Start To Start",
  };

  const linkSaveHandler = async () => {
    const formattedLinks = [
      ...existingDependencies,
      ...links
        .filter((item) => item.id && item.scopeId)
        .map((item) => {
          const selectedTask = availableTasksForScope.find(
            (t) => t.id === item.id
          );
          return {
            task: {
              id: item.id,
              name: selectedTask?.name || "",
            },
            dependencyType: parseInt(item.type, 10),
            scopeId: item.scopeId,
          };
        }),
    ];

    onSave(formattedLinks);
    setLinks([{ id: null, type: null, scopeId: null }]);
    setExistingDependencies(formattedLinks);
  };

  const handleAddTaskDependency = (index) => {
    const updatedLinks = [...links];
    updatedLinks[index + 1] = { id: null, type: null, scopeId: selectedScope };
    setLinks(updatedLinks);
  };

  const handleTaskChange = (value, type, index) => {
    const tempLinks = [...links];
    if (
      type === "id" &&
      tempLinks.some(
        (link) => link.id === value && link.scopeId === selectedScope
      )
    ) {
      dispatch(
        generalActions.showToast({
          title: "Task with this ID already exists in the selected scope.",
          type: "error",
        })
      );
      return;
    }

    tempLinks[index][type] = value;
    tempLinks[index].scopeId = selectedScope;
    if (type === "id") {
      const selectedTask = availableTasksForScope.find(
        (task) => task.id === value
      );
      if (selectedTask) {
        tempLinks[index].name = selectedTask.name;
      }
    }
    setLinks(tempLinks);
  };

  const handleDeleteLink = (index) => {
    const tempLinks = [...links];
    tempLinks.splice(index, 1);
    setLinks(tempLinks);
  };

  const handleRemoveExistingDependency = (index) => {
    const updatedDependencies = [...existingDependencies];
    updatedDependencies.splice(index, 1);
    setExistingDependencies(updatedDependencies);
  };

  const taskStatusMapper = {
    Inprogress: "In Progress",
    Monitoring: "Monitoring",
    Slipping: "Slipping",
    Completed: "Completed",
    Onhold: "On Hold",
  };

  const getStatus = (status) => taskStatusMapper[status] ?? "-";

  const getScopeName = (scopeId) => {
    const scope = scopes?.find((s) => s.id === scopeId);
    return scope?.name || "-";
  };

  return (
    <Modal
      size="small"
      body={
        <div className="flex flex-column gap-4">
          <div className={`${getTaskStatus(task.status).toLowerCase()} p-3`}>
            <div className="text-xsm-400 calanderHeadercolor">{task.name}</div>
            <span className="text-xsm-600 calanderHeadercolor">
              {getStatus(getTaskStatus(task.status))}
            </span>
          </div>

          {existingDependencies.length > 0 && (
            <div className="w-full flex flex-col gap-2">
              <h3 className="text-sm font-medium">Existing Dependencies</h3>
              {existingDependencies.map((dep, index) => (
                <div key={index} className="text-sm flex items-center gap-2 pl-2">
                  <p>{dep.task.name}</p>
                  {dep.scopeId && (
                    <p className="text-xs text-gray-600">
                      ({getScopeName(dep.scopeId)})
                    </p>
                  )}
                  <p>
                    {` -  ${dependencyTypeMapper[dep.dependencyType] || ""}`}
                  </p>
                  <div
                    className="cursor-pointer p-1"
                    onClick={() => handleRemoveExistingDependency(index)}
                  >
                    <Trash2 size={16} color="#d1424f"/>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="w-full flex flex-col gap-4">
            <h3 className="text-sm font-medium">Add New Dependencies</h3>
            <div className="w-full flex flex-column gap-2">
              <label className="text-sm font-medium">Scope</label>
              <Select
                onChange={(e) => setSelectedScope(e.target.value)}
                value={selectedScope || ""}
                styles={{ border: "none", height: 48 }}
              >
                <option value="" disabled>
                  Select Scope
                </option>
                {scopes?.map((scope) => (
                  <option key={scope.id} value={scope.id}>
                    {scope.name}
                  </option>
                ))}
              </Select>
            </div>

            {selectedScope ? (
              (() => {
                const scopeInfo = getScopeInfo(selectedScope);
                if (!scopeInfo.hasTasks) {
                  return (
                    <div className="w-full p-4 bg-yellow-50 border border-yellow-200 rounded text-center text-sm text-yellow-800">
                      The selected scope "{scopeInfo.scopeName}" has no tasks
                      available for dependency creation.
                      {scopeInfo.taskCount === 0
                        ? " This scope contains no tasks."
                        : " All tasks in this scope are already used or filtered out."}
                    </div>
                  );
                }

                return links.map((item, index) => (
                  <div key={index} className="w-full flex gap-4 items-end">
                    <div className="w-full flex flex-column gap-2">
                      <label className="text-sm font-medium">Task</label>
                      <Select
                        onChange={(e) =>
                          handleTaskChange(e.target.value, "id", index)
                        }
                        value={item.id || ""}
                        styles={{ border: "none", height: 48 }}
                        disabled={!selectedScope}
                      >
                        <option value="" disabled>
                          Select Task
                        </option>
                        {availableTasksForScope.map((task) => (
                          <option
                            key={task.id}
                            value={task.id}
                            disabled={links.some(
                              (linkTask, linkIndex) =>
                                linkTask.id === task.id && linkIndex !== index
                            )}
                          >
                            {task.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="w-full flex flex-column gap-2">
                      <label className="text-sm font-medium">
                        Dependency Type
                      </label>
                      <Select
                        onChange={(e) =>
                          handleTaskChange(e.target.value, "type", index)
                        }
                        value={item.type || ""}
                      >
                        <option value="" disabled>
                          Select Type
                        </option>
                        <option value="0">Finish To Start</option>
                        <option value="1">Start To Start</option>
                      </Select>
                    </div>
                    <div className="w-[80px] flex justify-center items-center gap-2 mb-2">
                      {/* Only show plus button if more than one task available */}
                      {availableTasksForScope.length > 1 &&
                        links.length < availableTasksForScope.length && (
                          <div
                            className="cursor-pointer pr-1"
                            onClick={() => handleAddTaskDependency(index)}
                          >
                            <Plus size={16} color="#342c82"/>
                          </div>
                        )}
                        <div
                          className="cursor-pointer"
                          onClick={() => handleDeleteLink(index)}
                        >
                          <Trash2 size={16} color="#d1424f"/>
                        </div>
                    </div>
                  </div>
                ));
              })()
            ) : (
              <div className="w-full p-4 bg-gray-100 rounded text-center text-sm text-gray-600">
                Please select a scope to view available tasks.
              </div>
            )}
          </div>
        </div>
      }
      handleCancel={() => {
        setLinks([{ id: null, type: null, scopeId: null }]);
        onClose();
      }}
      title="Add Dependency"
      saveBtnLabel="Save"
      handleSave={linkSaveHandler}
      cancelBtnLabel="Cancel"
      disableSave={
        !selectedScope ||
        availableTasksForScope.length === 0 ||
        !links.some((link) => link.id && link.type)
      }
    />
  );
}
